import { Grid } from '../core/Grid';
import { Cell } from '../core/types';
import { TemplateSegment, VariableRegistry, VariableValues, parseTemplate } from './Variables';

/**
 * Variable preview overlay.
 *
 * Placeholders live in the document as literal text, so rendering their values
 * must not mutate the document. This walks each row, finds `{{name}}` spans and
 * produces a sparse map of replacement cells the renderer draws on top — the
 * underlying design keeps the placeholder, which is what gets exported when the
 * project is used as a template.
 *
 * A value shorter than its placeholder is padded with spaces; a longer one
 * overflows to the right, which is exactly what the terminal would do at
 * runtime, so layout problems show up while designing rather than in
 * production.
 */
export function buildVariableOverlay(
  grid: Grid,
  registry: VariableRegistry,
  values: VariableValues,
  colorFromRules = true,
): Map<number, Cell> {
  const overlay = new Map<number, Cell>();

  for (let y = 0; y < grid.height; y++) {
    let row = '';
    for (let x = 0; x < grid.width; x++) {
      const ch = grid.getChar(x, y);
      row += ch === '' ? ' ' : ch;
    }
    if (!row.includes('{{')) continue;

    const segments = parseTemplate(row);
    let column = 0;
    for (const segment of segments) {
      if (segment.type === 'text') {
        column += [...segment.value].length;
        continue;
      }
      const placeholderWidth = [...(segment.raw ?? '')].length;
      const resolved = registry.format(segment.value, values, segment.formatters ?? []);
      const replacement = [...resolved.text];
      const style = styleAt(grid, column, y);

      const span = Math.max(placeholderWidth, replacement.length);
      for (let i = 0; i < span; i++) {
        const x = column + i;
        if (x >= grid.width) break;
        const char = i < replacement.length ? replacement[i] : ' ';
        overlay.set(y * grid.width + x, {
          char,
          fg: colorFromRules && resolved.color >= 0 ? resolved.color : style.fg,
          bg: style.bg,
          attrs: style.attrs,
        });
      }
      column += placeholderWidth;
    }
  }

  return overlay;
}

/** Style of the placeholder, taken from its first cell. */
function styleAt(grid: Grid, x: number, y: number): Cell {
  const cell = grid.get(Math.min(x, grid.width - 1), y);
  return cell;
}

/** True when the document contains at least one placeholder. */
export function hasVariables(grid: Grid): boolean {
  for (let y = 0; y < grid.height; y++) {
    let row = '';
    for (let x = 0; x < grid.width; x++) {
      const ch = grid.getChar(x, y);
      row += ch === '' ? ' ' : ch;
    }
    if (row.includes('{{')) return true;
  }
  return false;
}

/** Applies the overlay to a copy of the grid, for export and preview. */
export function applyOverlay(grid: Grid, overlay: Map<number, Cell>): Grid {
  if (overlay.size === 0) return grid;
  const out = grid.clone();
  for (const [index, cell] of overlay) out.setAt(index, cell);
  return out;
}

export type { TemplateSegment };
